import { Component, inject, NgZone, OnInit } from '@angular/core';
import { AnimationController, NavController, TransitionOptions } from '@ionic/angular';
import { StatusBar } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { register } from 'swiper/element/bundle';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { AudioService } from './services/audio.service';
import { Keyboard } from '@capacitor/keyboard';
import { AdminService } from './services/admin.service';
import { defineCustomElements } from 'state-stepper/loader';
import { AuthService } from './services/auth.service';
import { ProfileService } from './services/profile.service';
import { AuthFlowService } from './services/auth-flow.service';
import { AnalyticsService } from './services/analytics.service';
import { ConsentService } from './services/consent.service';
import { MetaPixelService } from './services/meta-pixel.service';
import { ModalController } from '@ionic/angular';
import { ConsentSheetComponent } from './components/consent-sheet/consent-sheet.component';
import { environment } from 'src/environments/environment';

register();
defineCustomElements(window);

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  private noMusicPages: string[] = ['mission', 'choose-melowish', 'ask-melowish', 'finally-select-melowish']; // Seiten ohne Musik
  private adminService = inject(AdminService)
  private authService = inject(AuthService)
  private authFlowService = inject(AuthFlowService)
  private profileService = inject(ProfileService)
  private consentService = inject(ConsentService)
  private metaPixelService = inject(MetaPixelService)
  private modalCtrl = inject(ModalController)

  constructor(
    private platform: Platform,
    private animationCtrl: AnimationController,
    private zone: NgZone,
    private navCtrl: NavController,
    private router: Router,
    private audioService: AudioService,
    private analytics: AnalyticsService,
    private route: ActivatedRoute,

  ) {
    this.initializeApp();
  }

  ngOnInit() {
    this.analytics.init();

    // Pixel lauscht auf die Einwilligung und laedt sich erst, wenn sie erteilt
    // ist. Ohne metaPixelId passiert hier ohnehin nichts.
    this.metaPixelService.init();
    void this.maybeAskForConsent();

    // Überwache Navigation, um Musik zu steuern
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = event.urlAfterRedirects.split('/')[1]; // Aktuelle Route
        if(!this.audioService.musicDisabled) {
          if (this.noMusicPages.includes(currentRoute)) {
            this.audioService.stop(); // Musik stoppen
          } else {
            // this.audioService.play(); // Musik starten
          }
        }
      }
    });
  }

  /**
   * Fragt die Einwilligung ab, wenn noch keine Entscheidung vorliegt.
   *
   * Haengt an environment.consentRequired: solange das false ist, wird nicht
   * gefragt und First-Touch unbedingt gespeichert - bewusst so fuer die
   * Testphase. Der Meta-Pixel feuert davon unabhaengig nur bei echter
   * Einwilligung, ein Flag ersetzt keine Rechtsgrundlage.
   */
  private async maybeAskForConsent(): Promise<void> {
    if (!environment.consentRequired) {
      return;
    }
    if (!this.consentService.needsDecision()) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: ConsentSheetComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      // Ohne Entscheidung wuerde die Abfrage bei jedem Aufruf erneut kommen.
      backdropDismiss: false,
      cssClass: 'consent-modal',
    });

    await modal.present();
  }

  /**
   * Zielroute aus einem Deep Link ziehen.
   *
   * Vorher stand hier `event.url.split("/#").pop()`. Mit Path-Routing enthaelt
   * die URL kein "/#" mehr, wodurch split die komplette URL zurueckgegeben
   * haette - inklusive Schema und Host - und die Navigation ins Leere gelaufen
   * waere.
   *
   *   https://app.melowish.com/present/abc?x=1  ->  /present/abc?x=1
   *   https://app.melowish.com/#/present/abc    ->  /present/abc   (Altlast)
   */
  private extractDeepLinkSlug(rawUrl: string): string | null {
    try {
      const url = new URL(rawUrl);

      // Alte Links, bei denen die Route im Fragment steckt.
      if (url.hash.indexOf('#/') === 0) {
        return url.hash.slice(1);
      }

      const slug = url.pathname + url.search;
      return slug && slug !== '/' ? slug : null;
    } catch {
      console.warn('appUrlOpen: URL nicht parsebar', rawUrl);
      return null;
    }
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.setStatusBarColor();
    });

    this.authFlowService.init();
    this.authService.authState$.subscribe(async user => {
      if (!user) {
        // globales Verhalten bei Logout
        // this.navCtrl.navigateRoot(['/home']);
      } else {
          await this.profileService.ensureProfile(user);
          // await this.navCtrl.navigateRoot('/home');
      }
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
          const slug = this.extractDeepLinkSlug(event.url);
          if (slug) {
              this.navCtrl.navigateRoot(slug);
          }
          // Kein Treffer: normales Routing uebernimmt.
      });
    });

    this.platform.backButton.subscribeWithPriority(10, () => {
      if (this.router.url === '/home') {
        App.minimizeApp(); // App minimieren, wenn wir auf der Startseite sind
      } else {
        this.navCtrl.back(); // Zurück navigieren
      }
    });

    App.getState()

    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        console.log('App ist im VORDERGRUND!');
        if(this.audioService.isPaused && this.platform.is("mobile")) {
          this.audioService.play();
        }
      } else {
        console.log('App ist im HINTERGRUND!');
        if(this.audioService.isPlaying && this.platform.is("mobile")) {
          this.audioService.pause();
        }
      }
    });

    // Keyboard.addListener('keyboardWillShow', (info) => {
    //   // console.log('🔼 Tastatur geöffnet! Höhe:', info.keyboardHeight);
    //   this.adminService.keyboardOpened = true;
    //   this.adjustKeyboardTranslation("open", info.keyboardHeight);
    // });

    // Keyboard.addListener('keyboardWillHide', () => {
    //   // console.log('🔽 Tastatur geschlossen!');
    //   this.adminService.keyboardOpened = false;
    //   this.adjustKeyboardTranslation("close");
    // });
  }

  adjustKeyboardTranslation(keyboardState: "open" | "close", keyboardHeight: number = 0) {
    const bottomDivs = document.querySelectorAll('.bottomDiv');
    const mainContainers = document.querySelectorAll('.mainContainer');
    const alertWrappers = document.querySelectorAll('.keyboard-alert');

    const bottomDiv = bottomDivs[bottomDivs.length-1] as HTMLElement;
    const mainContainer = mainContainers[mainContainers.length-1] as HTMLElement;
    const alertWrapper = alertWrappers[alertWrappers.length-1] as HTMLElement;

    if (!bottomDiv || !mainContainer) {
      console.warn('adjustKeyboardTranslation() - Kein `.bottomDiv` oder `.mainContainer` gefunden.');
      return;
    }

    if(keyboardState == "close") {
      mainContainer.style.transition = 'transform 0.25s ease-out';
      mainContainer.style.transform = `translateY(0)`;
      bottomDiv.style.transition = 'transform 0.25s ease-out';
      bottomDiv.style.transform = `translateY(0)`;
      if (alertWrapper) {
        alertWrapper.style.transition = 'transform 0.25s ease-out';
        alertWrapper.style.transform = `translateY(0)`;
      }
    } else {

      this.adminService.keyboardHeight = keyboardHeight;

      if (alertWrapper) {
        alertWrapper.style.transition = 'transform 0.2s ease-out';
        alertWrapper.style.transform = `translateY(-${keyboardHeight/2}px)`;
        return;
      }

      const movedBottomDivTop = bottomDiv.getBoundingClientRect().top-keyboardHeight;
      const mainContainerBottom = mainContainer.getBoundingClientRect().bottom;
      const availableSpace = movedBottomDivTop - mainContainerBottom;

      // console.log("bottomDivTop",bottomDiv.getBoundingClientRect().top)
      // console.log("movedBottomDivTop",movedBottomDivTop)
      // console.log("mainContainerBottom",mainContainerBottom)
      // console.log(`📏 Platz zwischen mainContainer und bottomDiv: ${availableSpace}px`);

      let preventBottomDivTransform = false;

      if (availableSpace < 50) {
        // Falls weniger als 50px Platz vorhanden ist, nach oben verschieben
        const translateYValue = 15 - availableSpace; // 15px Puffer

        const activeEl = document.activeElement as HTMLElement;
        if (!activeEl || !activeEl.getBoundingClientRect) return;
        const inputRect = activeEl.getBoundingClientRect();
        const movedInputRect = inputRect.top-translateYValue;

        console.log("inputRect.top",inputRect.top)
        console.log("inputRect.top-translateYValue",movedInputRect)
        console.log("window.innerHeight",window.innerHeight)

        if(movedInputRect > 130) {
          // Eingabefeld noch im sichtbaren Bereich (oberer Rand)
          // console.log(`🔝 mainContainer nach oben verschieben um: ${translateYValue}px`);
          mainContainer.style.transition = this.platform.is("android") ? 'transform 0.18s ease-out' : 'transform 0.25s ease-out';
          mainContainer.style.transform = `translateY(-${translateYValue}px)`;
        } else {
          preventBottomDivTransform = true;
        }
      }
      if(!preventBottomDivTransform) {
        bottomDiv.style.transition = this.platform.is("android") ? 'transform 0.18s ease-out' : 'transform 0.25s ease-out';
        bottomDiv.style.transform = `translateY(-${keyboardHeight}px)`;
      }
    }
  }

  async setStatusBarColor() {
    // await StatusBar.setBackgroundColor({ color: "#358AC9" });
  }

  myTransition = ((baseEl: HTMLElement, opts: TransitionOptions) => {
    const enteringEl: HTMLElement = opts.enteringEl; //Entering Element - New Page
    const enteringAnim = this.animationCtrl.create()
    .addElement(enteringEl)
    .duration(450)
    .iterations(1)
    .easing('ease-out')
    .keyframes([
      { offset: 0, opacity: '0.0' },
      { offset: 0.4, opacity: '0.0' },
      { offset: 1, opacity: "1" },
    ])

    if(opts.leavingEl) {
      const leavingEl: HTMLElement = opts.leavingEl; //Leaving Element - Current Page
      // opts.leavingEl.classList.add("ion-page-hidden")
      if(opts.direction && opts.direction == "forward") {
        const enteringForwardAnim = this.animationCtrl.create()
          .addElement(enteringEl)
          .duration(300)
          .iterations(1)
          .easing('ease-out')
          .keyframes([
            { offset: 0, transform: `translateY(${leavingEl.clientWidth*0.2}px)`, opacity: "0.0" },
            { offset: 0.3, transform: `translateY(${leavingEl.clientWidth*0.2}px)`, opacity: "0.0" },
            { offset: 1, transform: `translateX(0px)`, opacity: "1" },
          ])
        const leavingForwardAnim = this.animationCtrl.create()
          .addElement(leavingEl)
          .duration(200)
          .iterations(1)
          .easing('ease-out')
          .fromTo('opacity', '1', '0.0');

          var comnbinedForwardAnim = this.animationCtrl.create()
          .iterations(1)
          .addAnimation([leavingForwardAnim, enteringForwardAnim]);
        } else { // back transition
          const leavingBackAnim = this.animationCtrl.create()
            .addElement(leavingEl)
            .duration(200)
            .iterations(1)
            .easing('ease-out')
            .keyframes([
              { offset: 0, transform: `translateX(0px)`, opacity: "1" },
              { offset: 1, transform: `translateY(${leavingEl.clientWidth*0.2}px)`, opacity: "0.0" },
            ])
          var comnbinedForwardAnim = this.animationCtrl.create()
          .iterations(1)
          .addAnimation([leavingBackAnim, enteringAnim]);
        }
        return comnbinedForwardAnim;
    } else {
      return enteringAnim;
    }
  });
}
